import { dispatch, make, Payload } from "vuex-pathify"
import _ from "lodash"

import AdsAPI from "@/api/ads"

import { MODEL_TEMPLATE } from "./defaults"
import * as audienceActions from "@/store/modules/audiences/actions"
import * as audienceMutations from "@/store/modules/audiences/mutations"
import * as targetingActions from "@/store/modules/targeting/actions"
import * as targetingMutations from "@/store/modules/targeting/mutations"
import {
  SET_MODEL,
  SET_MODEL_ONLY,
  SET_MODEL_CHANGED,
  SET_MODEL_AUDIENCE,
} from "@/store/shared/mutations"

import {
  modelTextArrayAppend,
  modelTextArrayRemove,
  modelTextArrayUpdate,
} from "@/store/shared/actions"

const adDraftModelFromResponse = function(response) {
  return {
    id: response.id,
    adAccountId: response.adAccountId,
    isPublished: !!response.isPublished,
    ...response.draftData,
  }
}

const state = () => {
  return {
    ads: [],
    model: {},
    loading: false,
    datePreset: "maximum",
  }
}

const actions = {
  modelTextArrayAppend,
  modelTextArrayRemove,
  modelTextArrayUpdate,
  ...audienceActions,
  ...targetingActions,

  clearModule({ commit }) {
    commit("SET_ADS", [])
    commit("SET_MODEL", {})
    commit("SET_LOADING", false)
  },

  clearAdModel({ commit, rootGetters }) {
    let model = _.cloneDeep(MODEL_TEMPLATE)

    model.adAccountId = rootGetters["accounts/adAccountId"]

    commit("SET_MODEL", model)
    commit("SET_MODEL_CHANGED", false)
  },

  clearAdModelId({ commit }) {
    commit("SET_MODEL", new Payload(null, "id", null))
  },

  loadAdDraft({ commit }, id) {
    return AdsAPI.getAdDraft(id).then(response => {
      commit("SET_MODEL_ONLY", adDraftModelFromResponse(response))
      commit("SET_MODEL_CHANGED", false)
    })
  },

  loadAds({ commit, rootGetters, state }, force = false) {
    if (state.ads.length && !force) {
      return
    }

    commit("SET_LOADING", true)

    const adAccountId = rootGetters["accounts/adAccountId"]

    return AdsAPI.getAdAccountAds(adAccountId, { datePreset: state.datePreset })
      .then(result => {
        commit("SET_ADS", result)
      })
      .finally(() => {
        commit("SET_LOADING", false)
      })
  },

  patchAdStatus({ commit }, item) {
    return AdsAPI.patchAdStatus(item.id).then(response => {
      commit("UPDATE_AD", response)
    })
  },

  publishAdDraft({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      if (state.model._changed) {
        resolve(
          dispatch("saveAdDraft").then(() => {
            return dispatch("publishAdDraft")
          })
        )
      } else {
        AdsAPI.publishAdDraft(state.model.id)
          .then(response => {
            commit("SET_MODEL_ONLY", adDraftModelFromResponse(response))
            resolve(response)
          })
          .catch(error => {
            dispatch("loadAdDraft", state.model.id)
            reject(error)
          })
      }
    })
  },

  saveAudienceDraft({ commit, dispatch, state }) {
    return dispatch("audiences/saveAudienceDraft", state.model, {
      root: true,
    }).then(response => {
      commit("SET_MODEL_ONLY", new Payload(null, "audience", response))
    })
  },

  saveAdDraft({ commit, state }) {
    let data = {
      adAccountId: state.model.adAccountId,
      draftData: { ...state.model },
    }
    let dataClone = _.cloneDeep(data)

    delete dataClone.draftData.id
    delete dataClone.draftData.adAccountId
    delete dataClone.draftData._changed
    delete dataClone.draftData.isPublished

    if (!state.model.id) {
      return AdsAPI.postAdDraft(dataClone).then(response => {
        commit("SET_MODEL_ONLY", adDraftModelFromResponse(response))
        commit("SET_MODEL_CHANGED", false)
      })
    } else {
      return AdsAPI.patchAdDraft(state.model.id, dataClone).then(response => {
        commit("SET_MODEL_ONLY", adDraftModelFromResponse(response))
        commit("SET_MODEL_CHANGED", false)
      })
    }
  },
}

const getters = {
  ...make.getters(state),

  interestsNames: state => items => {
    const names = []

    items.forEach(i => {
      names.push(i.name)
    })

    return names.join(",")
  },
}

const mutations = {
  ...make.mutations(state),
  ...audienceMutations,
  ...targetingMutations,

  UPDATE_AD(state, payload) {
    state.ads = state.ads.map(i => {
      if (i.id === payload.id) {
        return _.merge(i, payload)
      }

      return i
    })
  },

  SET_MODEL,
  SET_MODEL_CHANGED,
  SET_MODEL_ONLY,
  SET_MODEL_AUDIENCE,
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}
